import { useEffect, useState } from 'react'
import { BookMarked, Lock, Plus, Trash2 } from 'lucide-react'
import { api, type ListSummary } from '../lib/api.ts'
import { Link, useRouter } from '../lib/router.tsx'
import { Shell } from '../components/Shell.tsx'
import { Button } from '../components/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog.tsx'
import { Input, Label, Select } from '../components/ui/input.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { UnlockDialog } from '../components/LockBanner.tsx'
import { Badge } from '../components/ui/badge.tsx'
import type { Theme } from '../lib/theme.ts'
import { plural } from '../lib/utils.ts'

export function ListsPage({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const [lists, setLists] = useState<ListSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const reload = () => api.lists().then(setLists).catch((e) => setError(String(e.message ?? e)))
  useEffect(() => void reload(), [])

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title="Moje seznamy četby"
      subtitle="Každý seznam je jeden výběr děl z kánonu, kontrolovaný proti maturitním kritériím."
      actions={
        <Button onClick={() => setCreating(true)}>
          <Plus size={15} aria-hidden /> Nový seznam
        </Button>
      }
    >
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      {lists === null && <p className="text-[var(--muted-foreground)]">Načítám…</p>}

      {lists?.length === 0 && (
        <Card className="py-10 text-center sm:py-14">
          <CardContent className="pt-0">
            <BookMarked size={32} className="mx-auto mb-3 text-[var(--muted-foreground)]" aria-hidden />
            <p className="mb-4 text-[var(--muted-foreground)]">Zatím nemáš žádný seznam.</p>
            <Button onClick={() => setCreating(true)}>
              <Plus size={15} aria-hidden /> Založit první seznam
            </Button>
          </CardContent>
        </Card>
      )}

      {lists && lists.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {lists.map((list) => (
            <li key={list.id}>
              <ListCard list={list} onChanged={reload} />
            </li>
          ))}
        </ul>
      )}

      <NewListDialog open={creating} onOpenChange={setCreating} onCreated={reload} />
    </Shell>
  )
}

function ListCard({ list, onChanged }: { list: ListSummary; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const count = list.selectedWorkIds.length
  // Mazání se řídí zámkem seznamu, ne správcovským režimem: seznam bez hesla
  // smaže kdokoli, seznam s heslem až po jeho zadání.
  const needsPassword = list.hasPassword && !list.canEdit

  return (
    <Card className="transition-shadow hover:shadow-sm">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle>
            <Link to={`/lists/${list.id}`} className="hover:underline">{list.name}</Link>
          </CardTitle>
          {list.hasPassword && (
            <Badge title="Seznam je chráněný heslem">
              <Lock size={10} aria-hidden /> zamčeno
            </Badge>
          )}
        </div>
        {list.studentName && <p className="text-[length:var(--text-sm)] text-[var(--muted-foreground)]">{list.studentName}</p>}
      </CardHeader>

      <CardContent className="space-y-3">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="tabular text-[length:var(--text-sm)]">{plural(count, 'dílo', 'díla', 'děl')} z 20</span>
            <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
              {count >= 20 ? 'plný počet' : `zbývá ${20 - count}`}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--border)]">
            <div
              className="h-full rounded-full bg-[var(--work-selected)] transition-[width]"
              style={{ width: `${Math.min(100, (count / 20) * 100)}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline" className="flex-1">
            <Link to={`/lists/${list.id}`}>Otevřít</Link>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={needsPassword ? `Odemknout a smazat seznam ${list.name}` : `Smazat seznam ${list.name}`}
            className="text-[var(--destructive)]"
            title={needsPassword ? 'Seznam je zamčený — smazání vyžaduje jeho heslo' : 'Smazat seznam'}
            onClick={() => (needsPassword ? setUnlocking(true) : setConfirming(true))}
          >
            <Trash2 size={14} aria-hidden />
          </Button>
        </div>
      </CardContent>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Smazat seznam?"
        description={
          <>
            Seznam „{list.name}" se smaže i s výběrem {plural(count, 'díla', 'děl', 'děl')}.
            Tohle se nedá vrátit.
          </>
        }
        confirmLabel="Smazat seznam"
        destructive
        onConfirm={async () => {
          await api.deleteList(list.id)
          onChanged()
        }}
      />

      <UnlockDialog
        listId={list.id}
        open={unlocking}
        onOpenChange={setUnlocking}
        description="Seznam je chráněný heslem. Zadej ho a rovnou se zeptáme na potvrzení smazání."
        onUnlocked={() => {
          onChanged()
          setConfirming(true)
        }}
      />
    </Card>
  )
}

function NewListDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => void }) {
  const { navigate } = useRouter()
  const [canons, setCanons] = useState<{ id: string; name: string }[]>([])
  const [name, setName] = useState('')
  const [studentName, setStudentName] = useState('')
  const [canonId, setCanonId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    api.canons().then((cs) => {
      setCanons(cs)
      setCanonId((prev) => prev || cs[0]?.id || '')
    }).catch((e) => setError(String(e.message ?? e)))
  }, [open])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const list = await api.createList({
        name: name.trim(),
        studentName: studentName.trim() || null,
        canonId,
        password: password || null,
      })
      onOpenChange(false)
      setName(''); setStudentName(''); setPassword('')
      onCreated()
      navigate(`/lists/${list.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Založení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nový seznam</DialogTitle>
        <DialogDescription>Vyber kánon školy; kritéria se dají upravit později v Kritériích.</DialogDescription>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-name">Název seznamu</Label>
            <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus placeholder="Maturita 2026" maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-student">Jméno studenta</Label>
            <Input id="new-student" value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Nepovinné — objeví se v exportu" maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-canon">Kánon</Label>
            <Select id="new-canon" value={canonId} onChange={(e) => setCanonId(e.target.value)} required className="w-full">
              {canons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Heslo</Label>
            <Input id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Nepovinné — chrání jen úpravy" />
            <p className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
              Bez hesla může seznam upravovat kdokoli, kdo zná odkaz. Čtení a tisk jsou volné vždy.
            </p>
          </div>

          {error && <Alert variant="destructive">{error}</Alert>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Zrušit</Button>
            <Button type="submit" disabled={busy || !name.trim() || !canonId}>{busy ? 'Zakládám…' : 'Založit'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
